/**
 * Report-only charts: a donut with a value legend, and horizontal bars for
 * "by campaign / by source" breakdowns (plain or stacked across metrics).
 * Hand-made SVG and CSS, like charts.tsx — the app carries no chart library.
 */

import type { ReactNode } from 'react';

import { seriesTone } from '../charts.tsx';
import type { CatalogueMetric } from './reports-api.ts';

export function Donut({
  slices,
  height,
  format,
  ring = true,
}: {
  slices: { label: string; value: number; tone: string }[];
  height: number;
  format: (value: number) => string;
  /** False draws a solid pie instead of a ring. */
  ring?: boolean;
}): ReactNode {
  const total = slices.reduce((sum, slice) => sum + Math.max(0, slice.value), 0);
  if (total <= 0) return <div className="rw-note">Nothing to split in this period.</div>;
  const size = Math.min(height - 8, 180);
  // A pie is a ring whose stroke is as wide as its radius: one drawing method for both.
  const outer = size / 2 - 10;
  const radius = ring ? outer : outer / 2;
  const stroke = ring ? 18 : outer;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="rw-donut">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={slices.map((slice) => `${slice.label} ${format(slice.value)}`).join(', ')}
      >
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {slices.map((slice) => {
            const length = (Math.max(0, slice.value) / total) * circumference;
            const dash = `${length} ${circumference - length}`;
            const node = (
              <circle
                key={slice.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={slice.tone}
                strokeWidth={stroke}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return node;
          })}
        </g>
        {ring && (
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="central"
            className="rw-donut-total"
          >
            {format(total)}
          </text>
        )}
      </svg>
      <ul className="rw-legend">
        {slices.map((slice) => (
          <li key={slice.label}>
            <span className="rw-dot" style={{ background: slice.tone }} aria-hidden="true" />
            <span className="rw-legend-label">{slice.label}</span>
            <span className="mono">{format(slice.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HorizontalBars({
  rows,
  metrics,
  stacked,
  format,
}: {
  rows: { label: string; values: Record<string, number | null> }[];
  metrics: CatalogueMetric[];
  stacked: boolean;
  format: (value: number, metric: CatalogueMetric) => string;
}): ReactNode {
  const shown = stacked ? metrics : metrics.slice(0, 1);
  const totalOf = (row: (typeof rows)[number]) =>
    shown.reduce((sum, metric) => sum + Math.max(0, row.values[metric.key] ?? 0), 0);
  const highest = Math.max(1, ...rows.map(totalOf));
  // No value at all (a roll-up client without this source) reads "—", not 0.
  const valueText = (value: number | null, metric: CatalogueMetric) =>
    value === null ? '—' : format(value, metric);
  if (rows.length === 0) return <div className="rw-note">Nothing to compare in this period.</div>;
  return (
    <div className="rw-hbars">
      {stacked && (
        <ul className="rw-legend rw-legend-inline">
          {shown.map((metric, index) => (
            <li key={metric.key}>
              <span
                className="rw-dot"
                style={{ background: seriesTone(index) }}
                aria-hidden="true"
              />
              {metric.label}
            </li>
          ))}
        </ul>
      )}
      {rows.map((row) => (
        <div className="rw-hbar" key={row.label}>
          <span className="rw-hbar-label" title={row.label}>
            {row.label}
          </span>
          <span className="rw-hbar-track">
            {shown.map((metric, index) => (
              <span
                key={metric.key}
                className="rw-hbar-fill"
                style={{
                  width: `${(Math.max(0, row.values[metric.key] ?? 0) / highest) * 100}%`,
                  background: seriesTone(index),
                }}
                title={`${metric.label}: ${format(row.values[metric.key] ?? 0, metric)}`}
              />
            ))}
          </span>
          <span className="rw-hbar-value mono">
            {stacked
              ? format(totalOf(row), shown[0] as CatalogueMetric)
              : valueText(row.values[shown[0]?.key ?? ''] ?? null, shown[0] as CatalogueMetric)}
          </span>
        </div>
      ))}
    </div>
  );
}
